import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGracePeriodStatus,
  getInventoryBillingInvoices,
  triggerBillingChecks,
  createMissingInvoices,
  updateLocationStorageSettings,
  inventoryBillingApi,
  type GetInventoryBillingInvoicesRequest,
} from "./inventory-billing.api";

export const INVENTORY_BILLING_KEYS = {
  all: ["inventory-billing"] as const,
  gracePeriodStatus: () =>
    [...INVENTORY_BILLING_KEYS.all, "grace-period-status"] as const,
  invoices: (params: GetInventoryBillingInvoicesRequest = {}) =>
    [...INVENTORY_BILLING_KEYS.all, "invoices", params] as const,
  itemsWithoutLocation: () =>
    [...INVENTORY_BILLING_KEYS.all, "items-without-location"] as const,
};

export const useGracePeriodStatus = () => {
  return useQuery({
    queryKey: INVENTORY_BILLING_KEYS.gracePeriodStatus(),
    queryFn: getGracePeriodStatus,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useItemsWithoutLocation = () => {
  return useQuery({
    queryKey: INVENTORY_BILLING_KEYS.itemsWithoutLocation(),
    queryFn: async () => {
      const { data } = await inventoryBillingApi.getItemsWithoutLocation();
      return data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useTriggerBillingChecks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerBillingChecks,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: INVENTORY_BILLING_KEYS.all });
    },
  });
};

export const useInventoryBillingInvoices = (params: GetInventoryBillingInvoicesRequest) => {
  return useQuery({
    queryKey: INVENTORY_BILLING_KEYS.invoices(params),
    queryFn: () => getInventoryBillingInvoices(params),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useCreateMissingInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMissingInvoices,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: INVENTORY_BILLING_KEYS.all });
    },
  });
};

export const useUpdateLocationStorageSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLocationStorageSettings,
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: INVENTORY_BILLING_KEYS.all });
    },
  });
};
